import React, {
  Suspense,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { LinearProgress } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import DashboardRouteShell from 'common/Skeletons/DashboardRouteShell';
import { useTypedSelector } from 'redux/reducers';
import PersistentDashboardContext, {
  PersistentDashboardOptions
} from './PersistentDashboardContext';
import { getDashboardRouteTitle } from './routeTitles';

const DashboardFrame = React.lazy(async () => {
  const dashboardModule = await import('./index');

  return { default: dashboardModule.DashboardFrame };
});

const PersistentDashboardRoute = ({ children }: React.PropsWithChildren<{}>) => {
  const location = useLocation();
  const processesName = useTypedSelector(
    (state) => state.config.instanceSettings.ProcessesName
  );
  const routeTitle = getDashboardRouteTitle(location.pathname, processesName);
  const [options, setOptions] = useState<PersistentDashboardOptions>({
    title: routeTitle
  });
  const registeredPath = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (registeredPath.current !== location.pathname) {
      setOptions({ title: routeTitle });
    }
  }, [location.pathname, routeTitle]);

  const registerOptions = useCallback(
    (nextOptions: PersistentDashboardOptions) => {
      const alreadyRegistered = registeredPath.current === location.pathname;
      registeredPath.current = location.pathname;
      setOptions((currentOptions) => ({
        title:
          nextOptions.title ??
          (alreadyRegistered ? currentOptions.title : routeTitle),
        padding: nextOptions.padding
      }));
    },
    [location.pathname, routeTitle]
  );
  const contextValue = useMemo(
    () => ({ registerOptions }),
    [registerOptions]
  );

  return (
    <PersistentDashboardContext.Provider value={contextValue}>
      <Suspense fallback={<DashboardRouteShell />}>
        <DashboardFrame title={options.title} padding={options.padding}>
          <Suspense fallback={<LinearProgress />}>
            {children}
          </Suspense>
        </DashboardFrame>
      </Suspense>
    </PersistentDashboardContext.Provider>
  );
};

export default PersistentDashboardRoute;
